import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import type { ReserveOptionMenuInput } from '~/lib/graphql/graphql'
import { useBack } from '~/navigation'
import { atomWithError } from './error'
import { selectedOptionMenus$ } from './stateMachine/context'
import type { ReserveInputOptionMenu } from './stateMachine/state'
import { optionMenus$, useTransition } from './stateMachine/state'

export type SelectedOptionMenuWithDetails = ReturnType<
  typeof selectedOptionMenusWithDetails
>[number]

export const selectedOptionMenusWithDetails$ = atomWithError<SelectedOptionMenuWithDetails[] | []>(
  (get) => {
    const options = get(optionMenus$)
    const selectedOptions = get(selectedOptionMenus$)
    return selectedOptionMenusWithDetails(options, selectedOptions)
  }
)
selectedOptionMenusWithDetails$.debugLabel = 'selectedOptionMenusWithDetails$'

function selectedOptionMenusWithDetails(
  options: ReserveInputOptionMenu[],
  selectedOptions: ReserveOptionMenuInput[]
) {
  return selectedOptions.map((selectedOption) => {
    const option = options.find(
      (option) => option.optionMenu.optionMenuId === selectedOption.optionMenuId
    )
    if (option === undefined) {
      throw new Error('selected option does not exists in options')
    }
    return {
      optionMenuId: selectedOption.optionMenuId,
      name: option.optionMenu.name,
      price: option.optionMenu.price,
      count: selectedOption.count,
    }
  })
}
if (import.meta.vitest) {
  const { expect, it } = import.meta.vitest
  const options = [
    { optionMenu: { optionMenuId: '1', name: 'option1', price: 1000 } },
    { optionMenu: { optionMenuId: '2', name: 'option2', price: 2000 } },
  ] as ReserveInputOptionMenu[]
  it('selectedOptionMenusWithDetails', () => {
    expect(selectedOptionMenusWithDetails([], [])).toEqual([])
    expect(selectedOptionMenusWithDetails(options, [])).toEqual([])
    expect(
      selectedOptionMenusWithDetails(options, [
        { optionMenuId: '1', count: 1 },
      ] as ReserveOptionMenuInput[])
    ).toEqual([{ optionMenuId: '1', name: 'option1', price: 1000, count: 1 }])
    expect(
      selectedOptionMenusWithDetails(options, [
        { optionMenuId: '1', count: 1 },
        { optionMenuId: '2', count: 2 },
      ] as ReserveOptionMenuInput[])
    ).toEqual([
      { optionMenuId: '1', name: 'option1', price: 1000, count: 1 },
      { optionMenuId: '2', name: 'option2', price: 2000, count: 2 },
    ])
    expect(() =>
      selectedOptionMenusWithDetails(options, [
        { optionMenuId: '3', count: 3 },
      ] as ReserveOptionMenuInput[])
    ).toThrow()
  })
}

export function useSelectedOptionMenusWithDetails() {
  return useAtomValue(selectedOptionMenusWithDetails$) ?? []
}

export function useSetOptionMenu() {
  const send = useTransition()
  const back = useBack()
  return useCallback(
    (optionMenus: ReserveOptionMenuInput[]) => {
      send({
        type: 'updated',
        optionMenus,
      })
      back()
    },
    [back, send]
  )
}
